(() => {
  class ContainerBuilder {
    constructor(root) {
      this.root = root;
      this.rows = [...root.querySelectorAll('[data-cb-row]')];
      this.quantities = [...root.querySelectorAll('[data-cb-qty]')];
      this.blockOver = root.dataset.blockOverCapacity === 'true';
      this.minimumFill = this.number(root.dataset.minimumFill) || 98;
      this.standardDiscountPercent = this.number(root.dataset.containerDiscount);
      this.singleTwentyDiscountPercent = this.number(root.dataset.singleTwentyDiscount ?? 5);
      this.standardDiscountCode = root.dataset.containerDiscountCode || '';
      this.singleTwentyDiscountCode = root.dataset.singleTwentyDiscountCode || '';
      this.containerDiscountPercent = 0;
      this.containerDiscountCode = '';
      this.currency = root.dataset.currency || 'USD';
      this.containerAccess = root.dataset.containerAccess === 'true';
      this.root.dataset.cbVersion = '16';
      this.state = null;

      this.quantities.forEach((input) => input.addEventListener('input', () => this.calculate()));
      root.querySelectorAll('[data-cb-order-mode]').forEach((input) => input.addEventListener('change', () => {
        this.calculate();
        this.refreshPricePreviews();
      }));
      root.addEventListener('toggle', (event) => {
        if (event.target.open) this.refreshPricePreviews();
      }, true);
      root.querySelectorAll('[data-cb-container-count]').forEach((input) => input.addEventListener('input', () => this.calculate()));
      root.querySelectorAll('[data-cb-search]').forEach((input) => input.addEventListener('input', (event) => this.filter(event.target.value, event.target.closest('[data-cb-catalog-mode]'))));
      root.querySelector('[data-cb-clear]')?.addEventListener('click', () => this.clear());
      root.querySelector('[data-cb-add]')?.addEventListener('click', () => this.addToCart());

      this.initializeRows();
      this.calculate();
      this.refreshPricePreviews();
      this.loadClayPrices();
    }

    number(value) {
      const parsed = Number.parseFloat(value);
      return Number.isFinite(parsed) ? parsed : 0;
    }

    integer(value) {
      return Math.max(0, Math.floor(this.number(value)));
    }

    initializeRows() {
      this.rows.forEach((row) => {
        const cbm = this.boxCbm(row);
        const output = row.querySelector('[data-cb-row-cbm]');
        if (output) output.textContent = cbm > 0 ? cbm.toFixed(3) : 'Required';

      });
    }

    async loadClayPrices() {
      this.setPriceStatus('Loading Wholesale Discount prices');
      const config = await this.waitForClayConfig();
      if (!config?.shopDomain || !config?.token) {
        this.setPriceStatus('Wholesale pricing is unavailable. Enable the Wholesale app embed and sign in with an eligible wholesale account.');
        return;
      }

      const handles = [...new Set(this.rows.map((row) => row.dataset.productHandle).filter(Boolean))];
      const customerGroupId = window.clayCustomerTier?.customerGroupId || (this.root.dataset.customerLoggedIn === 'true' ? 'LOGGED_IN' : 'ALL');
      const customerTags = window.clayCustomerTags || [];
      const country = window.clayCountryIsoCode || null;

      try {
        for (let start = 0; start < handles.length; start += 10) {
          const batch = handles.slice(start, start + 10);
          const fields = batch.map((handle, index) => `p${index}: product(handle: ${JSON.stringify(handle)}) {
            variants(first: 250) { edges { node {
              id price { amount currencyCode } compareAtPrice { amount currencyCode }
              metafield(namespace: "app--173437812737--clay", key: "pricing_rule") { value }
            } } }
          }`).join('\n');
          const response = await fetch(`https://${config.shopDomain}/api/2024-07/graphql.json`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': config.token },
            body: JSON.stringify({ query: `query ContainerClayPrices($country: CountryCode) @inContext(country: $country) { ${fields} }`, variables: { country } })
          });
          const payload = await response.json();
          if (!response.ok || payload.errors) throw new Error('Wholesale Storefront API request failed');

          batch.forEach((handle, index) => {
            const variants = payload.data?.[`p${index}`]?.variants?.edges || [];
            variants.forEach(({ node }) => {
              const id = String(node.id).split('/').pop();
              this.rows.filter((row) => row.dataset.variantId === id).forEach((row) => {
              row._clay = {
                retail: Math.round(this.number(node.price?.amount) * 100),
                compareAt: Math.round(this.number(node.compareAtPrice?.amount) * 100),
                rule: this.selectClayRule(node.metafield?.value, id, customerGroupId, customerTags)
              };
              });
            });
          });
          this.refreshPricePreviews();
          this.calculate(false);
        }
        this.root.dataset.clayLoaded = 'true';
        this.refreshPricePreviews(true);
        this.calculate(false);
      } catch (error) {
        console.error('Container builder could not load Wholesale prices:', error);
        this.setPriceStatus('Wholesale pricing could not be loaded. Retail prices are shown; checkout will still validate the final price.');
      }
    }

    async waitForClayConfig() {
      for (let attempt = 0; attempt < 100; attempt += 1) {
        if (window.clayStorefrontConfig?.shopDomain && window.clayStorefrontConfig?.token) {
          return window.clayStorefrontConfig;
        }
        await new Promise((resolve) => window.setTimeout(resolve, 100));
      }
      return null;
    }

    selectClayRule(rawValue, variantId, customerGroupId, customerTags) {
      if (!rawValue) return null;
      let source;
      try { source = JSON.parse(rawValue); } catch { return null; }
      if (!Array.isArray(source) || !source.length) return null;

      let rules = source;
      if ('i' in source[0]) {
        rules = source.map((entry) => {
          const [group = '', tag = ''] = String(entry.i || '').split('~');
          return {
            ownerId: entry.o || '', customerGroupId: group, customerTag: tag,
            markets: String(entry.p || '').split('|').map((market) => {
              const [handle = '', currency = '', strategy = 'A', unique = '0', priceSource = 'R', values = ''] = market.split('~');
              return { handle, currency, pricing: { strategy, isVariantUnique: unique === '1', priceSource, values: values.split(';').filter(Boolean).map((value) => {
                const [quantity = '1', price = '0'] = value.split(':');
                return { quantity, price };
              }) } };
            })
          };
        });
      } else {
        rules = source.map((entry) => ({
          ownerId: '', customerGroupId: entry.customerGroupId || '', customerTag: entry.customerTag || '',
          markets: [{ handle: '', currency: '', pricing: {
            strategy: 'A', priceSource: 'R', isVariantUnique: Boolean(entry.quantityPrice?.isVariantUnique),
            values: entry.type === 'WHOLESALE'
              ? [{ quantity: '1', price: entry.wholesalePrice }]
              : (entry.quantityPrice?.value || [])
          } }]
        }));
      }

      let rule = rules.find((item) =>
        (item.customerGroupId === customerGroupId && !['LOGGED_IN', 'ALL'].includes(customerGroupId)) ||
        (item.customerTag && customerTags.includes(item.customerTag))
      );
      if (!rule && customerGroupId !== 'ALL') rule = rules.find((item) => item.customerGroupId === 'LOGGED_IN');
      if (!rule) rule = rules.find((item) => item.customerGroupId === 'ALL');
      if (!rule || (rule.ownerId && String(rule.ownerId) !== String(variantId))) return null;
      const market = rule.markets.find((item) => item.handle === window.clayMarketHandle || item.handle === '');
      return market ? market.pricing : null;
    }

    clayPrice(row, linePieces, orderPieces) {
      const clay = row._clay;
      if (!clay?.rule?.values?.length) return null;
      const rule = clay.rule;
      // A zero-quantity row previews one full box. Never add this preview to totals.
      const previewPieces = this.integer(row.dataset.units) || 1;
      const quantity = rule.isVariantUnique
        ? (linePieces || previewPieces)
        : (linePieces ? orderPieces : Math.max(orderPieces, previewPieces));
      const tier = rule.values
        .filter((item) => this.number(item.quantity) <= quantity)
        .sort((a, b) => this.number(b.quantity) - this.number(a.quantity))[0];
      if (!tier) return null;
      const source = rule.priceSource === 'C' && clay.compareAt ? clay.compareAt : clay.retail;
      const value = this.number(tier.price);
      let cents = rule.strategy === 'P' ? source * (1 - value / 100)
        : rule.strategy === 'F' ? source - value * 100
        : value * 100;
      cents = Math.round(cents);
      return cents >= 0 && cents <= clay.retail ? cents : clay.retail;
    }

    setPriceStatus(message) {
      const output = this.root.querySelector('[data-cb-price-status]');
      if (output) output.textContent = message;
    }

    formatMoney(cents) {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency', currency: this.currency
      }).format((cents || 0) / 100);
    }

    unitPrices(row, linePieces = 0, orderPieces = 0) {
      const original = this.integer(row.dataset.originalPriceCents);
      const clayFinal = this.clayPrice(row, linePieces, orderPieces);
      return { original, final: clayFinal ?? original };
    }

    refreshPricePreviews(force = false) {
      const totals = {};
      this.rows.forEach((row) => {
        const pieces = this.integer(row.querySelector('[data-cb-qty]')?.value) * (this.integer(row.dataset.units) || 1);
        totals[row.dataset.orderMode] = (totals[row.dataset.orderMode] || 0) + pieces;
      });
      this.rows.forEach((row) => {
        const pieces = this.integer(row.querySelector('[data-cb-qty]')?.value) * (this.integer(row.dataset.units) || 1);
        this.syncClayQuantity(row, pieces, force);
        const output = row.querySelector('[data-cb-final-price]');
        if (output) {
          output.textContent = this.formatMoney(this.unitPrices(row, pieces, totals[row.dataset.orderMode]).final);
          output.title = pieces ? 'Unit price for the entered quantity' : 'Unit price preview for one full box; no boxes added';
        }
      });
    }

    syncClayQuantity(row, pieces, force = false) {
      const pricingPieces = pieces || this.integer(row.dataset.units) || 1;
      const clayInput = row.querySelector('.cb__clay-qty');
      row.querySelectorAll('[data-wpd-cart-drawer-quantity]').forEach((element) => {
        element.dataset.wpdCartDrawerQuantity = String(pricingPieces);
      });
      if (clayInput && (force || this.integer(clayInput.value) !== pricingPieces)) {
        clayInput.value = String(pricingPieces);
        clayInput.dispatchEvent(new Event('input', { bubbles: true }));
        clayInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    boxCbm(row) {
      return this.number(row.dataset.length) * this.number(row.dataset.width) * this.number(row.dataset.height) / 1000000;
    }

    selectedContainers() {
      const twenty = this.integer(this.root.querySelector('[data-cb-container-count="20"]')?.value);
      const forty = this.integer(this.root.querySelector('[data-cb-container-count="40hc"]')?.value);
      return { twenty, forty, total: twenty + forty };
    }

    discountSelection(containers) {
      if (!containers.total) return { percent: 0, code: '' };
      const singleTwenty = containers.twenty === 1 && containers.forty === 0;
      return singleTwenty
        ? { percent: this.singleTwentyDiscountPercent, code: this.singleTwentyDiscountCode }
        : { percent: this.standardDiscountPercent, code: this.standardDiscountCode };
    }

    selectedOrderMode() {
      return this.root.querySelector('[data-cb-order-mode]:checked')?.value || '';
    }

    calculate(syncClay = true) {
      const mode = this.selectedOrderMode();
      const isContainerOrder = mode === 'container';
      const containers = this.selectedContainers();
      const discount = this.discountSelection(isContainerOrder ? containers : { total: 0 });
      this.containerDiscountPercent = discount.percent;
      this.containerDiscountCode = discount.code;
      const capacity = isContainerOrder
        ? containers.twenty * this.number(this.root.dataset.capacityTwenty) + containers.forty * this.number(this.root.dataset.capacityForty)
        : 0;
      const payload = isContainerOrder
        ? containers.twenty * this.number(this.root.dataset.payloadTwenty) + containers.forty * this.number(this.root.dataset.payloadForty)
        : 0;
      const restricted = isContainerOrder && !this.containerAccess;
      const activeRows = this.rows.filter((row) => row.dataset.orderMode === mode && !restricted);
      // Refresh hidden catalogs too, so loading Wholesale does not require a quantity edit.
      this.rows.filter((row) => !activeRows.includes(row)).forEach((row) => {
        const output = row.querySelector('[data-cb-final-price]');
        if (output) output.textContent = this.formatMoney(this.unitPrices(row).final);
      });
      let boxes = 0;
      let pieces = 0;
      let volume = 0;
      let weight = 0;
      let missingDimensions = false;
      let inventoryError = false;
      let originalTotal = 0;
      let finalTotal = 0;
      const items = [];

      const orderPieces = activeRows.reduce((total, row) => {
        const qty = this.integer(row.querySelector('[data-cb-qty]')?.value);
        return total + qty * (this.integer(row.dataset.units) || 1);
      }, 0);

      activeRows.forEach((row) => {
        const input = row.querySelector('[data-cb-qty]');
        const qty = this.integer(input?.value);
        if (input && String(qty) !== input.value) input.value = String(qty);
        row.classList.toggle('cb__row--selected', qty > 0);
        const message = row.querySelector('[data-cb-row-message]');
        if (message) message.textContent = '';
        const units = this.integer(row.dataset.units) || 1;
        const cbm = this.boxCbm(row);
        const linePieces = qty * units;
        const available = row.dataset.availablePieces;
        if (input) {
          if (mode === 'box' && available !== '') input.max = String(Math.floor(this.integer(available) / units));
          else input.removeAttribute('max');
        }
        if (syncClay) this.syncClayQuantity(row, linePieces);
        const prices = this.unitPrices(row, linePieces, orderPieces);
        const finalPriceOutput = row.querySelector('[data-cb-final-price]');
        if (finalPriceOutput) finalPriceOutput.textContent = this.formatMoney(prices.final);
        const lineOriginal = linePieces * prices.original;
        const lineFinal = linePieces * prices.final;
        const originalOutput = row.querySelector('[data-cb-line-original]');
        const finalOutput = row.querySelector('[data-cb-line-final]');
        if (originalOutput) originalOutput.textContent = this.formatMoney(lineOriginal);
        if (finalOutput) finalOutput.textContent = this.formatMoney(lineFinal);
        row.classList.toggle('cb__row--discounted', prices.final < prices.original && qty > 0);
        if (!qty) return;

        if (isContainerOrder && cbm <= 0) {
          missingDimensions = true;
          if (message) message.textContent = 'Box dimensions required';
        }
        if (mode === 'box' && available !== '' && linePieces > this.integer(available)) {
          inventoryError = true;
          if (message) message.textContent = `Only ${available} pieces are currently in stock`;
        }
        boxes += qty;
        pieces += linePieces;
        volume += qty * cbm;
        weight += qty * this.number(row.dataset.weight);
        originalTotal += lineOriginal;
        finalTotal += lineFinal;
        items.push({ row, boxes: qty, pieces: linePieces, cbm: qty * cbm, originalCents: lineOriginal, finalCents: lineFinal });
      });

      const percent = capacity ? (volume / capacity) * 100 : 0;
      const volumeOver = volume > capacity + 0.0001;
      const weightOver = payload > 0 && weight > payload;
      const over = volumeOver || weightOver;
      const missingContainer = isContainerOrder && containers.total === 0;
      const underMinimum = isContainerOrder && !missingContainer && percent < this.minimumFill;
      const invalid = missingDimensions || inventoryError;
      const discountQualified = isContainerOrder && !restricted && !missingContainer && percent >= this.minimumFill;
      const containerDiscount = discountQualified ? Math.round(finalTotal * this.containerDiscountPercent / 100) : 0;
      const payableTotal = Math.max(0, finalTotal - containerDiscount);

      this.state = { mode, restricted, isContainerOrder, containers, capacity, payload, boxes, pieces, volume, weight, percent, over, missingContainer, underMinimum, invalid, items, originalTotal, clayTotal: finalTotal, discountQualified, discountPercent: discount.percent, discountCode: discount.code, containerDiscount, finalTotal: payableTotal };
      this.render();
    }

    render() {
      const s = this.state;
      const fillDisplay = Math.floor(s.percent * 10) / 10;
      const set = (selector, value) => {
        const element = this.root.querySelector(selector);
        if (element) element.textContent = value;
      };
      const containerParts = [];
      if (s.containers?.twenty) containerParts.push(`${s.containers.twenty}× 20-foot`);
      if (s.containers?.forty) containerParts.push(`${s.containers.forty}× 40HC`);
      const name = !s.mode ? 'Select an order type' : s.isContainerOrder
        ? (containerParts.join(' + ') || 'Select container quantities') : 'Wholesale Box Order';
      this.root.classList.toggle('cb--mode-unselected', !s.mode);
      this.root.classList.toggle('cb--box-order', s.mode === 'box');
      this.root.classList.toggle('cb--container-order', s.isContainerOrder);
      this.root.classList.toggle('cb--container-restricted', s.restricted);
      set('[data-cb-container-name]', name);
      set('[data-cb-product-step]', s.isContainerOrder ? '3' : '2');
      set('[data-cb-summary-step]', s.isContainerOrder ? '4. Review container and pricing' : '3. Review box order');
      set('[data-cb-used]', s.volume.toFixed(2));
      set('[data-cb-capacity]', s.capacity.toFixed(2));
      set('[data-cb-percent]', `${fillDisplay}%`);
      set('[data-cb-boxes]', s.boxes.toLocaleString());
      set('[data-cb-pieces]', s.pieces.toLocaleString());
      set('[data-cb-weight]', Math.round(s.weight).toLocaleString());
      set('[data-cb-remaining]', Math.max(0, s.capacity - s.volume).toFixed(2));
      set('[data-cb-original-total]', this.formatMoney(s.originalTotal));
      set('[data-cb-discount]', `− ${this.formatMoney(Math.max(0, s.originalTotal - s.clayTotal))}`);
      const volumeToQualify = Math.max(0, s.capacity * this.minimumFill / 100 - s.volume);
      set('[data-cb-discount-progress]', !s.containers.total
        ? `Fill at least ${this.minimumFill}% to qualify: exactly one 20-foot container receives ${this.singleTwentyDiscountPercent}%; one 40HC or any selection of two or more containers receives ${this.standardDiscountPercent}%. Discounts apply after your Wholesale Discount.`
        : s.discountQualified
        ? `You qualify! Your additional ${this.containerDiscountPercent}% Container Discount is included in the estimated total below.`
        : `Fill at least ${this.minimumFill}% of your selected containers to receive an additional ${this.containerDiscountPercent}% discount after your Wholesale Discount.${s.capacity ? ` Current fill: ${fillDisplay}%. Add ${(Math.ceil(volumeToQualify * 100) / 100).toFixed(2)} CBM more to qualify.` : ''}`);
      set('[data-cb-container-discount-label]', !s.containers.total ? 'Container Discount — Select containers' : `Container Discount (${this.containerDiscountPercent}%)${s.discountQualified ? ' — Qualified' : ` — Available at ${this.minimumFill}% fill`}`);
      set('[data-cb-container-discount]', `− ${this.formatMoney(s.containerDiscount)}`);
      set('[data-cb-final-total]', this.formatMoney(s.finalTotal));
      if (this.root.dataset.clayLoaded === 'true') {
        set('[data-cb-price-status]', !s.pieces
          ? 'Unit prices preview one full box. Totals stay zero until you enter boxes.'
          : s.originalTotal > s.clayTotal
          ? 'Wholesale Discount has been applied for this customer and quantity.'
          : 'no eligible Wholesale Discount for this customer and quantity.');
      }

      const visualPercent = Math.min(100, Math.max(0, s.percent));
      const fill = this.root.querySelector('[data-cb-fill]');
      const meter = this.root.querySelector('[data-cb-meter]');
      if (fill) fill.style.setProperty('--fill', `${visualPercent}%`);
      if (meter) meter.style.width = `${visualPercent}%`;
      this.root.classList.toggle('cb--warning', s.percent >= 85 && !s.over);
      this.root.classList.toggle('cb--over', s.over);

      const notice = this.root.querySelector('[data-cb-notice]');
      const add = this.root.querySelector('[data-cb-add]');
      const discountCodeMissing = s.isContainerOrder && this.containerDiscountPercent > 0 && !this.containerDiscountCode;
      if (!s.mode) {
        notice.textContent = 'Choose Wholesale Box Order or Container Order to begin.';
      } else if (s.restricted) {
        notice.textContent = 'Container orders require an approved customer account.';
      } else if (s.missingContainer) {
        notice.textContent = 'Enter at least one 20-foot or 40HC container.';
      } else if (!s.items.length) {
        notice.textContent = 'Select products by entering box quantities.';
      } else if (s.invalid) {
        notice.textContent = 'Correct the highlighted product information before continuing.';
      } else if (s.isContainerOrder && s.over) {
        const reason = s.volume > s.capacity ? 'volume' : 'payload';
        notice.textContent = `This order exceeds the selected container ${reason}. Reduce boxes or choose another container.`;
      } else if (s.isContainerOrder && s.underMinimum) {
        notice.textContent = `Fill at least ${this.minimumFill}% to receive your additional ${this.containerDiscountPercent}% Container Discount and add this order to cart. Current fill: ${fillDisplay}%.`;
      } else if (discountCodeMissing) {
        notice.textContent = `The ${this.containerDiscountPercent}% container discount code must be configured before this order can be added. Please contact us.`;
      } else if (s.isContainerOrder) {
        notice.textContent = `Your additional ${this.containerDiscountPercent}% Container Discount is included. ${Math.max(0, s.capacity - s.volume).toFixed(2)} CBM remains in the selected containers.`;
      } else {
        notice.textContent = 'Box order is within available stock limits.';
      }
      if (add) {
        add.disabled = s.restricted || !s.mode || s.missingContainer || !s.items.length || s.invalid || discountCodeMissing || (s.isContainerOrder && (s.underMinimum || (this.blockOver && s.over)));
        add.textContent = s.isContainerOrder ? 'Add container order to cart' : 'Add box order to cart';
      }
    }

    filter(value, scope = this.root) {
      const query = value.trim().toLowerCase();
      scope.querySelectorAll('[data-cb-product-group]').forEach((productGroup) => {
        let productMatches = false;
        productGroup.querySelectorAll('[data-cb-color-group]').forEach((colorGroup) => {
          const colorRows = [...colorGroup.querySelectorAll('[data-cb-row]')];
          let colorMatches = false;
          colorRows.forEach((row) => {
            const matches = !query || row.dataset.search.includes(query);
            row.hidden = !matches;
            colorMatches ||= matches;
          });
          colorGroup.hidden = !colorMatches;
          if (query && colorMatches) colorGroup.open = true;
          productMatches ||= colorMatches;
        });
        productGroup.hidden = !productMatches;
        if (query && productMatches) productGroup.open = true;
      });
    }

    clear() {
      const mode = this.selectedOrderMode();
      this.rows.filter((row) => row.dataset.orderMode === mode).forEach((row) => {
        const input = row.querySelector('[data-cb-qty]');
        if (input) input.value = '0';
      });
      this.calculate();
    }

    async addToCart() {
      const button = this.root.querySelector('[data-cb-add]');
      const notice = this.root.querySelector('[data-cb-notice]');
      if (!this.state?.items.length || this.state.restricted || button.disabled) return;

      button.disabled = true;
      button.classList.add('is-loading');
      button.textContent = 'Adding order…';
      const isContainerOrder = this.state.isContainerOrder;
      const appliedDiscountPercent = this.state.discountPercent;
      const appliedDiscountCode = this.state.discountQualified ? this.state.discountCode : '';
      const containerParts = [];
      if (this.state.containers.twenty) containerParts.push(`${this.state.containers.twenty}x 20-foot`);
      if (this.state.containers.forty) containerParts.push(`${this.state.containers.forty}x 40HC`);
      const containerName = containerParts.join(' + ');
      const orderName = isContainerOrder ? containerName : 'Wholesale Box Order';
      const items = this.state.items.map(({ row, boxes, pieces, cbm }) => ({
        id: Number(row.dataset.variantId),
        quantity: pieces,
        properties: {
          'Order type': orderName,
          'Boxes': String(boxes),
          'Pieces per box': String(this.integer(row.dataset.units)),
          ...(isContainerOrder ? {
            'Estimated volume': `${cbm.toFixed(3)} CBM`,
            'Additional container discount': `${appliedDiscountPercent}%`
          } : {})
        }
      }));

      try {
        const rootPath = window.Shopify?.routes?.root || '/';
        for (let index = 0; index < items.length; index += 50) {
          const response = await fetch(`${rootPath}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: items.slice(index, index + 50) })
          });
          if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(error.description || error.message || 'Shopify could not add this order.');
          }
        }

        await fetch(`${rootPath}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            attributes: {
              'Order type': orderName,
              ...(isContainerOrder ? {
              'Estimated container CBM': this.state.volume.toFixed(2),
              'Estimated container weight kg': Math.round(this.state.weight).toString(),
              'Container discount requested': `${appliedDiscountPercent}%`
              } : {}),
              'Total boxes': this.state.boxes.toString()
            }
          })
        });
        window.location.href = appliedDiscountCode
          ? `${rootPath}discount/${encodeURIComponent(appliedDiscountCode)}?redirect=${encodeURIComponent(`${rootPath}cart`)}`
          : `${rootPath}cart`;
      } catch (error) {
        notice.textContent = error.message;
        button.disabled = false;
        button.classList.remove('is-loading');
        button.textContent = this.state.isContainerOrder ? 'Add container order to cart' : 'Add box order to cart';
      }
    }
  }

  const initialize = (scope = document) => {
    scope.querySelectorAll('[data-container-builder]:not([data-cb-ready])').forEach((root) => {
      root.dataset.cbReady = 'true';
      new ContainerBuilder(root);
    });
  };
  document.addEventListener('DOMContentLoaded', () => initialize());
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
